import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import WizardModeIndicator from '../../../../Util/WizardModeIndicator';
import { Pivot, PivotItem, Spinner } from "@fluentui/react";
import useAxios from "axios-hooks";
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import BasicInfoStep from "./BasicInfoStep";
import InternationalStep from "./InternationalStep";
import CobrandStep from './CobrandStep';
import ErrorComponent from '../../../../Util/ErrorComponent';
import SuccessComponent from '../../../../Util/SuccessComponent';
import ConfirmModal from './ConfirmModal';
import { Formik } from "formik";
import { ConfirmBasicInfo, ConfirmInternational, ConfirmCobrands } from '../Helpers/Modals';
import { buildBasicInfo, buildI18n, buildCobrands } from '../Helpers/CopyAndFunctions';
import NavButtonsGroup from '../Helpers/NavButtonsGroup';
import FeatureSummary from '../../../../Util/FeatureSummary';

const formCss = css`
    margin-top: 20px;
    .ms-Stack, .international-form {
        width: 80%;
        max-width: 900px;
    }
`;
const mapCategories = (categories) => categories.map((c) => {
    return {
        key: c.journalCategoryId,
        text: c.category
    }
});

export const CreateJournalWizard = () => {
    const history = useHistory();
    let { promptId: promptIdFromParams } = useParams();
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmArray, setConfirmArray] = useState();
    const [selectedKey, setSelectedKey] = useState(0);
    const [prevValues, setPrevValues] = useState();
    const journalId = promptIdFromParams;

    const [{ data }, submitBasicInfo] = useAxios(
        { url: 'journal/upsert' },
        { manual: true }
    );
    const [{ i18nSettings }, submitI18n] = useAxios(
        { url: 'journal/localize', method: "post" },
        { manual: true }
    );
    const [{ cobrandSettings }, submitCobrands] = useAxios(
        { url: 'journal/cobrands', method: "post" },
        { manual: true }
    );
    const [{ data: journal, loading: journalLoading }, getJournal] = useAxios(
        { url: `journal/details/${journalId}`, method: "get" },
        { manual: true }
    );
    const [{ data: allJournals, loading: allJournalsLoading }] = useAxios('journal/categoriesPromptsAudit');
    const [{ data: countries, loading: loadingCountries }] = useAxios('courses/countries');
    const [{ data: cobrands, loading: loadingCobrands }] = useAxios('internal/cobrands');

    const prevOnClick = () => setSelectedKey(Number(selectedKey) - 1);
    const nextOnClick = () => setSelectedKey(Number(selectedKey) + 1);
    const handleLinkClick = (item) => setSelectedKey(item.props.itemKey);

    const ready = journalId ? (journal && allJournals && countries && cobrands) : (allJournals && countries && cobrands);

    const buildConfirm = (values) => {
        if (!values.category || !values.prompt) {
            setShowError(true);
            setErrorMsg('Category and Prompt must have values.');
            return;
        }
        if (values.category || values.prompt) setShowError(false);

        if (Number(selectedKey) === 0) {
            const confirmArr = buildBasicInfo(journalId ? prevValues : values, values, !journalId);
            setConfirmArray(confirmArr);
            confirmArr.length === 0 ? nextOnClick() : setIsConfirmOpen(true);
        }

        if (Number(selectedKey) === 1) {
            const confirmArr = buildI18n(journalId ? prevValues : values, values, !journalId)
            setConfirmArray(confirmArr);
            confirmArr.length === 0 ? nextOnClick() : setIsConfirmOpen(true);
        }

        if (Number(selectedKey) === 2) {
            const confirmArr = buildCobrands(journalId ? prevValues : values, values, !journalId)
            setConfirmArray(confirmArr);
            confirmArr.length === 0 ? setSelectedKey(0) : setIsConfirmOpen(true);
        }
    }

    useEffect(() => {
        if (journalId) getJournal();
    }, []);

    useEffect(() => {
        if (journal && !journalLoading) setPrevValues(journal);
    }, [journalLoading, journal]);


    if (!ready) {
        return <Spinner className="absolute-center" text="Loading Journal..." />;
    }

    
    return (
        <>
            <WizardModeIndicator paramId={promptIdFromParams} title="Journal" />
        {ready && (
            <Formik
                initialValues={mapInitialValues({ initial: journal, caps: allJournals, countries: countries, cobrands: cobrands })}
                onSubmit={async (values) => {
                    let result;
                    setIsConfirmOpen(false);
                    
                    switch (parseInt(selectedKey)) {
                        case 0:
                            if (values.addCategory) values.journalCategoryId = null;

                            result = await submitBasicInfo({
                                data: values,
                                method: "post",
                                url: 'journal/upsert',
                            });
                            if (result) {
                                if (!values.journalCategoryId) values.journalCategoryId = result.data.journalCategoryId;
                                if (!values.journalPromptId) values.journalPromptId = result.data.journalPromptId;
                                values.categories = mapCategories(result.data.categories);
                                values.prompts = result.data.prompts;
                                values.addCategory = false;
                                values.editCategory = false;
                                values.switchCategory = false;
                                nextOnClick();
                            }
                            break;

                        case 1:
                            result = await submitI18n({
                                data: values,
                                method: "post",
                                url: 'journal/localize',
                            });
                            if (result) nextOnClick();
                            break;
                        
                        case 2:
                            result = await submitCobrands({
                                data: values,
                                method: "post",
                                url: 'journal/cobrands',
                            });
                            if (result) {
                                if (!journalId) {
                                    setTimeout(
                                        () => history.replace('/mindfulness-feature/journals'),
                                        1000,
                                    );
                                } else {
                                    setSelectedKey(0);
                                }
                            }
                            break;

                        default:
                            break;
                    }
                    
                    if (!result.isAxiosError) {
                        setPrevValues(values);
                        setShowSuccess(true);
                        setTimeout(() => setShowSuccess(false), 5000);
                    } else {
                        setShowError(true);
                    }
                }}
                validateOnMount={false}
            >
            {({ dirty, isValid, values, handleSubmit }) => {
                return (
                    <>
                        <Pivot
                            selectedKey={String(selectedKey)}
                            onLinkClick={handleLinkClick}
                        >
                            <PivotItem headerText="Basic Info" itemKey="0">
                                <div css={formCss} >
                                    <BasicInfoStep
                                        disabled={!!journalId}
                                        model={values}
                                        isNew={!journalId}
                                    />
                                </div>
                            </PivotItem>
                        {values.journalPromptId && (
                            <PivotItem headerText="Localization" itemKey="1">
                                <div css={formCss} >
                                    <InternationalStep
                                        model={values}
                                        prevOnClick={prevOnClick}
                                        isNew={!journalId}
                                    />
                                </div>
                            </PivotItem>
                        )}
                        {values.journalPromptId && (
                            <PivotItem headerText="Cobrands On/Off" itemKey="2">
                                <div css={formCss} >
                                    <CobrandStep
                                        model={values}
                                        prevOnClick={prevOnClick}
                                        isNew={!journalId}
                                    />
                                </div>
                            </PivotItem>
                        )}
                        </Pivot>
                        <NavButtonsGroup
                            showNext={parseInt(selectedKey) === 0}
                            btnText1={parseInt(selectedKey) === 0 ? (dirty ? 'Save and Continue' : 'Next') : 'Prev'}
                            btnText2={parseInt(selectedKey) === 1 ? (dirty ? 'Save and Continue' : 'Next') : 'Save'}
                            nextClick={!dirty ? nextOnClick : () => buildConfirm(values)} 
                            prevClick={prevOnClick}
                            disabled={!dirty && parseInt(selectedKey) === 2}
                        />
                        <ConfirmModal
                            isConfirmOpen={isConfirmOpen}
                            closeConfirmModal={() => setIsConfirmOpen(false)}
                            handleSubmit={handleSubmit}
                        >
                            {parseInt(selectedKey) === 0 && (
                                <ConfirmBasicInfo arr={confirmArray} val={values} />
                            )}
                            {parseInt(selectedKey) === 1 && (
                                <ConfirmInternational arr={confirmArray} />
                            )}
                            {parseInt(selectedKey) === 2 && (
                                <ConfirmCobrands arr={confirmArray} />
                            )}
                        </ConfirmModal>
                        <SuccessComponent
                            successMessage="Journal Updated."
                            showSuccess={showSuccess}
                            setShowSuccess={setShowSuccess}
                        />
                        <ErrorComponent
                            errorMessage={errorMsg}
                            showError={showError}
                            setShowError={setShowError}
                        />
                        <FeatureSummary
                            label="About Journal Prompts"
                            summary="Create or edit journal prompts for the mindfulness feature. Configure the category, prompt text, international availability settings, and cobrand visibility. Journal prompts help users reflect on their financial wellness journey and personal goals."
                        />
                    </>
                    );
                }}
            </Formik>
        )}
        </>
    );
};

function mapInitialValues(data) {
    if (data?.initial?.journalPromptId && data?.caps?.categories && data?.countries) {
        return { ...data.initial, 
            checklistOnly: data.initial.checklistOnly ?? false,
            includeCountries: data.initial.includeCountries || [], 
            excludeCountries: data.initial.excludeCountries || [],
            includeCobrands: data.initial.includeCobrands || [], 
            excludeCobrands: data.initial.excludeCobrands || [], 
            addCategory: false, 
            editCategory: false, 
            switchCategory: false, 
            categories: mapCategories(data.caps.categories), 
            prompts: data.caps.prompts,
            countries: data.countries.map(c => { return {value: c.id, label: c.name} }),
            cobrands: data.cobrands.map(c => { return {value: c.cobrandId, label: c.name} })
        };
    }
    
    if (data?.caps?.categories && data?.countries) {
        return {
            journalPromptId: null, 
            journalCategoryId: null,
            prompt: '', 
            category: '', 
            international: true, 
            isGlobal: true,
            allCobrands: true,
            checklistOnly: false,
            addCategory: true,
            editCategory: false,
            switchCategory: false,
            includeCountries: [],
            excludeCountries: [],
            includeCobrands: [],
            excludeCobrands: [],
            categories: mapCategories(data.caps.categories),
            prompts: data.caps.prompts,
            countries: data.countries.map(c => { return {value: c.id, label: c.name} }),
            cobrands: data.cobrands.map(c => { return {value: c.cobrandId, label: c.name} })
        };
    }
}

export default CreateJournalWizard;
